import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  api,
  type Category,
  type Media,
  type Project,
  type Visibility,
  mediaSrc,
} from "../../lib/api";

const empty = {
  title: "",
  description: "",
  shortDescription: "",
  date: "",
  software: "",
  externalLinksText: "",
  featured: false,
  displayPriority: 0,
  visibility: "draft" as Visibility,
  categoryIds: [] as string[],
  tagNames: "",
};

export function AdminProjectEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState<Category[]>([]);
  const [media, setMedia] = useState<Media[]>([]);
  const [thumbnailId, setThumbnailId] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.adminCategories().then(setCategories);
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.adminProject(id!).then((p: Project) => {
      setForm({
        title: p.title,
        description: p.description,
        shortDescription: p.shortDescription || "",
        date: p.date ? p.date.slice(0, 10) : "", // API sends YYYY-MM-DD
        software: (p.software || []).join(", "),
        externalLinksText: (p.externalLinks || [])
          .map((l) => `${l.label}|${l.url}`)
          .join("\n"),
        featured: p.featured,
        displayPriority: p.displayPriority,
        visibility: p.visibility,
        categoryIds: p.categories.map((c) => c.id),
        tagNames: p.tags.map((t) => t.name).join(", "),
      });
      setMedia(p.media || []);
      setThumbnailId(p.thumbnailId || p.thumbnail?.id || null);
    });
  }, [id, isNew]);

  function parseLinks() {
    return form.externalLinksText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, ...rest] = line.split("|");
        return { label: label.trim(), url: rest.join("|").trim() };
      })
      .filter((l) => l.label && l.url);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setMsg(null);
    const body = {
      title: form.title,
      description: form.description,
      shortDescription: form.shortDescription || null,
      date: form.date || null,
      software: form.software
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      externalLinks: parseLinks(),
      featured: form.featured,
      displayPriority: Number(form.displayPriority) || 0,
      visibility: form.visibility,
      categoryIds: form.categoryIds,
      tagNames: form.tagNames
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      thumbnailId,
    };
    try {
      if (isNew) {
        const p = await api.createProject(body);
        setMsg("Created");
        navigate(`/admin/projects/${p.id}`, { replace: true });
      } else {
        const p = await api.updateProject(id!, body);
        setMedia(p.media || []);
        setMsg("Saved");
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onUpload(files: FileList | null) {
    if (!files?.length || isNew) {
      setErr("Save the project first, then upload media.");
      return;
    }
    for (const file of Array.from(files)) {
      const m = await api.uploadMedia(file, id);
      setMedia((prev) => [...prev, m]);
      if (!thumbnailId && m.type === "image") {
        await api.setThumbnail(id!, m.id);
        setThumbnailId(m.id);
      }
    }
    setMsg("Upload complete");
  }

  async function addVideo() {
    if (!videoUrl || isNew) return;
    const m = await api.addVideoLink(videoUrl, id);
    setMedia((prev) => [...prev, m]);
    setVideoUrl("");
    setMsg("Video link added");
  }

  async function removeMedia(mid: string) {
    await api.deleteMedia(mid);
    setMedia((prev) => prev.filter((m) => m.id !== mid));
    if (thumbnailId === mid) setThumbnailId(null);
  }

  async function makeThumb(mid: string) {
    if (isNew) return;
    await api.setThumbnail(id!, mid);
    setThumbnailId(mid);
  }

  return (
    <div className="admin-page">
      <h1>{isNew ? "New project" : "Edit project"}</h1>
      <p className="admin-page__sub">
        <Link to="/admin/projects">← Projects</Link>
      </p>
      {msg && <div className="admin-msg admin-msg--ok">{msg}</div>}
      {err && <div className="admin-msg admin-msg--err">{err}</div>}

      <form className="admin-form admin-form--wide" onSubmit={save}>
        <div className="field">
          <label htmlFor="title">Title</label>
          <input
            id="title"
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="short">Short description</label>
          <input
            id="short"
            value={form.shortDescription}
            onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="desc">Description</label>
          <textarea
            id="desc"
            rows={8}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem" }}>
          <div className="field">
            <label htmlFor="date">Project date (YYYY-MM-DD)</label>
            <input
              id="date"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
            <p className="field__hint">
              Chronological creation date. Used for home Recent and portfolio date sort.
              Public site shows month and year only.
            </p>
          </div>
          <div className="field">
            <label htmlFor="pri">Display priority</label>
            <input
              id="pri"
              type="number"
              value={form.displayPriority}
              onChange={(e) =>
                setForm({ ...form, displayPriority: parseInt(e.target.value, 10) || 0 })
              }
            />
          </div>
          <div className="field">
            <label htmlFor="vis">Visibility</label>
            <select
              id="vis"
              value={form.visibility}
              onChange={(e) =>
                setForm({ ...form, visibility: e.target.value as Visibility })
              }
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="private">Private</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            />{" "}
            Featured
          </label>
        </div>
        <div className="field">
          <label>Categories</label>
          <div className="admin-check-grid">
            {categories.map((c) => (
              <label key={c.id}>
                <input
                  type="checkbox"
                  checked={form.categoryIds.includes(c.id)}
                  onChange={(e) => {
                    setForm({
                      ...form,
                      categoryIds: e.target.checked
                        ? [...form.categoryIds, c.id]
                        : form.categoryIds.filter((x) => x !== c.id),
                    });
                  }}
                />
                {c.name}
              </label>
            ))}
          </div>
        </div>
        <div className="field">
          <label htmlFor="tags">Tags (comma-separated)</label>
          <input
            id="tags"
            value={form.tagNames}
            onChange={(e) => setForm({ ...form, tagNames: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="soft">Software / tools (comma-separated)</label>
          <input
            id="soft"
            value={form.software}
            onChange={(e) => setForm({ ...form, software: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="links">External links (one per line: Label|https://…)</label>
          <textarea
            id="links"
            rows={3}
            value={form.externalLinksText}
            onChange={(e) => setForm({ ...form, externalLinksText: e.target.value })}
          />
        </div>

        <div className="admin-page__toolbar">
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save project"}
          </button>
        </div>
      </form>

      {!isNew && (
        <section style={{ marginTop: "2rem" }}>
          <h2>Media</h2>
          <div className="admin-page__toolbar">
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={(e) => onUpload(e.target.files)}
            />
            <input
              placeholder="YouTube / Vimeo / video URL"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              style={{
                flex: 1,
                minWidth: 200,
                background: "var(--bg-elevated)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "0.55rem 0.8rem",
              }}
            />
            <button type="button" className="btn btn--ghost btn--sm" onClick={addVideo}>
              Add video link
            </button>
          </div>
          <div className="admin-media-grid">
            {media.map((m) => (
              <figure key={m.id}>
                {m.type === "image" ? (
                  <img src={mediaSrc(m.thumbnailUrl || m.url)} alt="" />
                ) : (
                  <div
                    style={{
                      aspectRatio: 1,
                      display: "grid",
                      placeItems: "center",
                      color: "var(--text-dim)",
                      fontSize: 12,
                    }}
                  >
                    {m.videoSource || "video"}
                  </div>
                )}
                <figcaption>
                  {m.type === "image" && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => makeThumb(m.id)}
                      style={{
                        opacity: thumbnailId === m.id ? 1 : 0.7,
                        color: thumbnailId === m.id ? "var(--accent)" : undefined,
                      }}
                    >
                      Thumb
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    onClick={() => removeMedia(m.id)}
                  >
                    ×
                  </button>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
